import React from 'react';

/**
 * Wrap a component to add loading state management.
 *
 * @param WrappedComponent {React.Component} react component to wrap.
 * @param promiseProducer {function(any): Promise<any>} function taking the component's props and
 * returning a promise. Typically, this promise will fetch data from the server.
 * @param initialState {Object?} initial state of the component.
 * @param finalStateProducer {function(any): Object?} function taking the result of the promise and
 * returning an object to pass to the component's state.
 * @param errorStateProducer {function(any): Object?} function taking the error that caused the
 * promise to be rejected and producing an object to pass to the component's state.
 * @param shouldReload {(function(any, any): boolean)?} function taking previous and current props.
 * Called in componentDidUpdate. If it returns true, data is reloaded.
 * @returns a component that wraps the given component, adding state management. On mount, the
 * initial state is set, the promise is resolved, the result passed to finalStateProducer, and its
 * return added to state. If shouldUpdate is given, the same loading logic is applied in
 * componentDidUpdate based on its return value.
 */
export default function withLoading({
  WrappedComponent,
  promiseProducer,
  initialState = {loading: true, loadingError: false},
  finalStateProducer = () => ({
    loading: false,
    loadingError: false,
  }),
  errorStateProducer = () => ({
    loading: false,
    loadingError: true,
  }),
  shouldReload = () => false,
}) {
  return class extends React.PureComponent {
    constructor(props) {
      super(props);
      this.state = initialState;
    }

    loadData() {
      setStateFromPromise(
        this,
        promiseProducer,
        initialState,
        finalStateProducer,
        errorStateProducer
      );
    }

    componentDidMount() {
      this.loadData();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      // Yes, this reloads all of the data. Use with care.
      if (shouldReload(prevProps, this.props)) {
        this.loadData();
      }
    }

    render() {
      const props = {
        ...this.state,
        ...this.props,
      };
      return <WrappedComponent {...props} />;
    }
  };
}

/**
 * Set component state, with loading state management.
 *
 * @param component {React.Component} the react component.
 * @param promiseProducer {function(any): Promise<any>} function taking the component's props and returning a promise.
 * @param initialState {Object?} state to set before calling the promise.
 * @param finalStateProducer {function(any): Object?} function taking the result of the promise and
 * returning an object to pass to the component's state.
 * @param errorStateProducer {function(any): Object?}
 */
export function setStateFromPromise(
  component,
  promiseProducer,
  initialState,
  finalStateProducer,
  errorStateProducer
) {
  const callback = () => {
    promiseProducer(component.props).then(
      result => {
        const state = finalStateProducer(result);
        state && component.setState(state);
      },
      error => {
        const state = errorStateProducer(error);
        state && component.setState(errorStateProducer(error));
      }
    );
  };
  component.setState(initialState, callback);
}
